import { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Bet } from 'types/Bet';
import { MatchOffer } from 'types/MatchOffer';
import { LiveEvent, LiveEventBet } from 'types/LiveEvent';
import { groupMarkets, MarketGroup } from 'helpers/marketGroups';
import { splitMarkets, type MarketCard } from 'helpers/marketLines';
import { useOfferSections } from 'helpers/offerLayout';
import { filterMarkets } from 'helpers/marketSearch';
import {
  MarketSearch,
  MarketSearchEmpty,
  MarketSearchToggle,
} from 'components/MarketSearch/MarketSearch';
import { OtherBets } from './OtherBets';
import styles from './GroupedOtherBets.module.css';

type Props =
  | { bets: Bet[]; match: MatchOffer; isLive?: false }
  | { bets: LiveEventBet[]; match: LiveEvent; isLive: true };

/**
 * A match's whole offer in sections (Popular, Handicap, Totals, 1st half, ...)
 * that open and close like betbigwin's: the first section is open, the rest
 * show their name and how many markets they hold until clicked. A match with
 * a single section is listed as it is.
 */
export const GroupedOtherBets = (props: Props) => {
  const { bets, match, isLive = false } = props;
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  // The sections and their order as the staff set them in the admin.
  const layout = useOfferSections(
    props.isLive ? props.match.sportId : props.match.league?.sport,
    isLive ? 'live' : 'prematch'
  );
  // One card per line ("Hendikep (0:1)", "Više/manje golova (2.5)"), then
  // the cards into their sections. Live markets come as they are.
  const cards: MarketCard[] = props.isLive
    ? (props.bets.filter((b) => b && b.odds?.length) as unknown as MarketCard[])
    : splitMarkets(
        props.bets.filter((b) => b && b.odds?.length),
        { home: match.home?.name, away: match.away?.name }
      );
  const found = filterMarkets(cards, query, t);
  const searching = query.trim() !== '';
  // Live names carry the teams ("Sporting Goals"): the layout names them
  // {home} / {away}.
  const teams = isLive
    ? { home: match.home?.name, away: match.away?.name }
    : undefined;
  const sections = groupMarkets(found, layout, teams);
  // Until the player opens or closes one, the first section is open - also
  // when the layout arrives after the first render and the sections change.
  const [picked, setOpen] = useState<Set<MarketGroup> | null>(null);
  const open =
    picked ??
    new Set(
      groupMarkets(cards, layout, teams)
        .slice(0, 1)
        .map((s) => s.group)
    );
  // A short offer needs no search. Closed, it is a magnifier at the left
  // of the toolbar; open, the field fills the toolbar beside "Open all", so
  // the search never takes a line of its own.
  const canSearch = cards.length > 8;
  const search = searchOpen && (
    <MarketSearch
      value={query}
      onChange={setQuery}
      found={found.length}
      onClose={() => setSearchOpen(false)}
    />
  );
  const searchToggle = canSearch && !searchOpen && (
    <MarketSearchToggle onClick={() => setSearchOpen(true)} />
  );
  const searchSlot = search || searchToggle;

  if (searching && !found.length)
    return (
      <div className={styles.groups}>
        <div className={styles.toolbar}>{search}</div>
        <MarketSearchEmpty query={query} />
      </div>
    );

  if (sections.length <= 1)
    return (
      <div className={styles.groups}>
        {searchSlot && <div className={styles.toolbar}>{searchSlot}</div>}
        <OtherBets
          bets={sections[0]?.bets ?? (bets as Bet[])}
          match={match}
          isLive={isLive}
        />
      </div>
    );

  // While searching every section with a match is open.
  const isOpen = (group: MarketGroup) => searching || open.has(group);
  const allOpen = sections.every((s) => isOpen(s.group));
  const toggle = (group: MarketGroup) =>
    setOpen(() => {
      const next = new Set(open);
      if (!next.delete(group)) next.add(group);
      return next;
    });

  return (
    <div className={styles.groups}>
      <div className={styles.toolbar}>
        {searchSlot}
        <button
          hidden={searching}
          type="button"
          className={styles.toggleAll}
          onClick={() =>
            setOpen(allOpen ? new Set() : new Set(sections.map((s) => s.group)))
          }
        >
          {allOpen
            ? t('market_groups_collapse_all')
            : t('market_groups_expand_all')}
        </button>
      </div>
      {sections.map(({ group, bets: groupBets }) => (
        <section key={group}>
          <button
            type="button"
            className={classNames(styles.header, {
              [styles.headerOpen]: isOpen(group),
            })}
            aria-expanded={isOpen(group)}
            onClick={() => toggle(group)}
          >
            <span className={styles.chevron} aria-hidden="true" />
            <span>
              {t(`market_group_${group}`)} ({groupBets.length})
            </span>
          </button>
          {isOpen(group) && (
            <OtherBets bets={groupBets} match={match} isLive={isLive} />
          )}
        </section>
      ))}
    </div>
  );
};
